<template>
  <DModal
    :show-close="false"
    :isBgOverlay="true"
    @close="$emit('close')"
    :appendToBody="true"
    :placeInTop="true"
    style="z-index: 12312312312312"
  >
    <div class="d-modal d-modal--full-width text-dark" slot="insertComponent" @click.stop="closeFilters">
      <div class="d-modal__header">
        <h2>Gennemsnits undervisningstal</h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content">
        <Loader v-if="isLoading"></Loader>
        <template v-else>
          <div class="avg-uv__toolbar">
            <div class="avg-uv__filters" @click.stop>
              <DoFiltersFilter
                :title="'Grupper'"
                :tags="uniqueGroups"
                :selectedTags="selectedGroups"
                :closeFromProp="closeFiltersCount"
                :numberOfSelectedTagsToShow="4"
                :placeholder="'Alle grupper'"
                @select="toggleGroup"
                @clear="clearGroups"
              ></DoFiltersFilter>
            </div>
            <button class="do-bttn do-bttn--submit" @click.stop="asyncLoadXLSXAndDownload">
              Download til Excel
            </button>
          </div>

          <div class="avg-uv__table-scroll">
            <table class="do-table do-table--striped">
              <thead>
                <tr>
                  <th>Navn</th>
                  <th class="avg-uv__narrow">Ansættelsesgrad</th>
                  <th class="avg-uv__narrow">Timeansættelse pr. uge</th>
                  <th class="avg-uv__narrow">Uv-timer</th>
                  <th class="avg-uv__narrow">Normeret til fuldtid</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredRows.length === 0">
                  <td colspan="5"><i>Ingen personer matcher filtrene</i></td>
                </tr>
                <tr v-for="row in filteredRows" :key="row.userId">
                  <td><strong>{{ row.name }}</strong></td>
                  <td class="avg-uv__narrow">{{ formatPercentage(row.employmentPercentage) }}</td>
                  <td class="avg-uv__narrow">{{ row.weeklyHours === null ? '' : formatDecimal(row.weeklyHours) }}</td>
                  <td class="avg-uv__narrow">{{ formatDecimal(row.teachingHours) }}</td>
                  <td class="avg-uv__narrow">{{ row.normalizedToFullTime === null ? '' : formatDecimal(row.normalizedToFullTime) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="filteredRows.length > 0">
                <tr class="avg-uv__summary">
                  <td colspan="4">
                    <strong>Gennemsnit (normeret) — {{ summary.count }} medarbejdere</strong>
                  </td>
                  <td class="avg-uv__narrow">
                    <strong>{{ summary.average === null ? '' : formatDecimal(summary.average) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </template>
      </div>

      <div class="d-modal__buttons">
        <button class="do-bttn" @click="$emit('close')">Luk</button>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../common/DModal.vue'
import Loader from '../common/Loader.vue'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import docendo from '../../docendo'

let XLSX = null

export default {
  components: {DModal, Loader, DoFiltersFilter},
  props: {
    schoolId: {
      default: () => null
    },
    schoolYearId: {
      default: () => null
    }
  },
  data () {
    return {
      isLoadingUserYears: false,
      userYears: [],
      teachingHoursMap: {},
      isLoadingTeachingHours: false,
      selectedGroups: [],
      closeFiltersCount: 0
    }
  },
  computed: {
    poolsDeactivated () {
      const settings = this.$store.state.schoolSettings
      return !!(settings && settings.deactivatePools)
    },
    users () {
      return this.$store.getters.usersSortedOnName
    },
    userYearMap () {
      const map = {}
      this.userYears.forEach(userYear => {
        map[userYear.user_id] = userYear
      })
      return map
    },
    rows () {
      return this.users.map(user => {
        const employmentPercentage = this.employmentPercentage(user.id)
        const teachingHours = this.assignedTeachingHours(user.id)
        return {
          userId: user.id,
          name: user.name,
          userTags: user.user_tags || [],
          employmentPercentage: employmentPercentage,
          weeklyHours: this.weeklyHours(employmentPercentage),
          teachingHours: teachingHours,
          normalizedToFullTime: this.normalizedToFullTime(teachingHours, employmentPercentage)
        }
      })
    },
    filteredRows () {
      return this.rows.filter(row => this.matchesGroups(row))
    },
    uniqueGroups () {
      let groups = []
      this.rows.forEach(row => {
        if (row.userTags && row.userTags.length > 0) {
          groups = groups.concat(row.userTags)
        }
      })
      return this.uniqueSorted(groups)
    },
    summary () {
      const normalizedRows = this.filteredRows.filter(row => row.normalizedToFullTime !== null)
      const count = normalizedRows.length
      if (count === 0) {
        return {count: 0, average: null}
      }
      const total = normalizedRows.reduce((sum, row) => sum + row.normalizedToFullTime, 0)
      return {count: count, average: total / count}
    },
    isLoading () {
      return this.isLoadingUserYears || (this.poolsDeactivated && this.isLoadingTeachingHours)
    }
  },
  methods: {
    uniqueSorted (items) {
      const seen = {}
      const unique = []
      items.forEach(item => {
        if (!seen[item]) {
          seen[item] = true
          unique.push(item)
        }
      })
      return unique.sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    matchesGroups (row) {
      if (this.selectedGroups.length < 1) return true
      if (!row.userTags || row.userTags.length < 1) return false
      return row.userTags.some(tag => this.selectedGroups.indexOf(tag) > -1)
    },
    toggleGroup (group) {
      const idx = this.selectedGroups.indexOf(group)
      if (idx > -1) {
        this.selectedGroups.splice(idx, 1)
      } else {
        this.selectedGroups.push(group)
      }
    },
    clearGroups () {
      this.selectedGroups = []
    },
    closeFilters () {
      this.closeFiltersCount++
    },
    assignedTeachingHours (userId) {
      if (this.poolsDeactivated) {
        const hours = this.teachingHoursMap[userId]
        return hours != null ? hours : 0
      }
      const userYear = this.userYearMap[userId]
      return userYear && userYear.assigned_teaching_hours ? userYear.assigned_teaching_hours : 0
    },
    normValue (userId, title) {
      const userYear = this.userYearMap[userId]
      const norm = userYear && userYear.norm ? userYear.norm : []
      const found = norm.find(item => item.title === title)
      return found ? found.value : null
    },
    employmentPercentage (userId) {
      const value = this.normValue(userId, 'hidden_ansættelsesgrad')
      return value !== null ? parseFloat(value) : null
    },
    weeklyHours (employmentPercentage) {
      if (employmentPercentage === null || isNaN(employmentPercentage) || employmentPercentage === 0) {
        return null
      }
      return (employmentPercentage / 100) * 37
    },
    normalizedToFullTime (teachingHours, employmentPercentage) {
      if (employmentPercentage === null || isNaN(employmentPercentage) || employmentPercentage === 0) {
        return null
      }
      const hours = parseFloat(teachingHours)
      if (isNaN(hours)) return null
      return (hours / employmentPercentage) * 100
    },
    formatDecimal (value) {
      const num = parseFloat(value)
      return isNaN(num) ? '' : num.toFixed(2).replace('.', ',')
    },
    formatPercentage (value) {
      if (value === null || value === undefined || isNaN(value) || value === 0) return ''
      return this.formatDecimal(value) + ' %'
    },
    readUserYears () {
      if (!this.schoolId || !this.schoolYearId) return
      this.isLoadingUserYears = true
      docendo.schoolUserYearsList(this.schoolId, this.schoolYearId, null, success => {
        this.userYears = success || []
        this.isLoadingUserYears = false
      }, failure => {
        console.log(failure)
        this.isLoadingUserYears = false
      })
    },
    readTeachingHours () {
      if (!this.schoolId || !this.schoolYearId) return
      this.isLoadingTeachingHours = true
      docendo.readUserHoursForSchoolYear(this.schoolId, this.schoolYearId, null, success => {
        const map = {}
        const rows = (success && success.teaching_hours) || []
        rows.forEach(row => {
          if (row.user_id == null) return
          map[row.user_id] = (map[row.user_id] || 0) + (parseFloat(row.hours) || 0)
        })
        this.teachingHoursMap = map
        this.isLoadingTeachingHours = false
      }, failure => {
        console.log(failure)
        this.isLoadingTeachingHours = false
      }, true)
    },
    asyncLoadXLSXAndDownload () {
      if (window.XLSX && XLSX) {
        this.download()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        let intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.download()
          }
        }, 500)
      }
    },
    download () {
      const rows = [
        ['Navn', 'Ansættelsesgrad', 'Timeansættelse pr. uge', 'Uv-timer', 'Normeret til fuldtid']
      ]
      this.filteredRows.forEach(row => {
        rows.push([
          row.name,
          row.employmentPercentage === null || isNaN(row.employmentPercentage) ? '' : row.employmentPercentage,
          row.weeklyHours === null ? '' : row.weeklyHours,
          isNaN(parseFloat(row.teachingHours)) ? '' : parseFloat(row.teachingHours),
          row.normalizedToFullTime === null ? '' : row.normalizedToFullTime
        ])
      })
      rows.push([
        'Gennemsnit (normeret) — ' + this.summary.count + ' medarbejdere',
        '',
        '',
        '',
        this.summary.average === null ? '' : this.summary.average
      ])
      const data = XLSX.utils.aoa_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, data, 'data')
      XLSX.writeFile(wb, 'docendo_gennemsnits_undervisningstal.xlsx')
    }
  },
  mounted () {
    this.readUserYears()
    if (this.poolsDeactivated) {
      this.readTeachingHours()
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-modal--full-width {
  width: calc(100vw - 2rem);
}

.d-modal__content {
  padding: 1rem;
  max-height: calc(100vh - 150px);
  overflow: hidden;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
}

.avg-uv__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.avg-uv__filters {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.avg-uv__table-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.avg-uv__table-scroll thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f6f9fc;
  box-shadow: inset 0 -1px 0 #e3e6ea;
}

.avg-uv__narrow {
  width: 160px;
}

.avg-uv__table-scroll tfoot tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}

.avg-uv__table-scroll tfoot {
  position: sticky;
  bottom: 0;
  z-index: 2;
}

.avg-uv__table-scroll tfoot td {
  padding: .5rem;
  background: #f6f9fc;
  box-shadow: inset 0 1px 0 #e3e6ea;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #eee;
}

.d-modal__header {
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// AvgTeachingHoursModal.vue?644eccca